// components/ProtectedRoute.tsx
import React from "react";
import { Navigate } from "react-router-dom";
import { CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import Header from "./layouts/Header";

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAuth: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requireAuth,
}) => {
    const { user, loading, error } = useAuth();

    const containerStyles: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        textAlign: "center",
        padding: "16px",
        position: "relative",
    };

    const headerContainerStyles: React.CSSProperties = {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
    };

    // Show loading spinner while checking auth
    if (loading) {
        return (
            <div style={containerStyles}>
                <div style={headerContainerStyles}>
                    <Header />
                </div>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Authenticating...</Typography>
            </div>
        );
    }

    const isAuthenticated = !!user;

    if (requireAuth) {
        // Page requires authentication
        if (!isAuthenticated) {
            // Not authenticated, render the message
            return (
                <div style={containerStyles}>
                    <div style={headerContainerStyles}>
                        <Header />
                    </div>
                    <Typography variant="h5">Access Denied</Typography>
                    <Typography sx={{ mt: 1 }}>
                        {error || "Please use a valid access token. The token should be included in the URL."}
                    </Typography>
                </div>
            );
        }
    } else {
        // Page doesn't require authentication (like login page)
        if (isAuthenticated) {
            // Already authenticated, redirect to dashboard
            return <Navigate to="/" />;
        }
    }

    // Render the children
    return <>{children}</>;
};

export default ProtectedRoute;
